import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { BriefApplicationResponseDto, BriefResponseDto } from '../../../../../../common/general-types/briefs.dto';
import { SubmittedPostDto, SubmittedPostStatusEnum } from '../../../../../../common/general-types/submitted-posts.dto';
import { VideoResponseDto } from '../../../../../../common/general-types/videos.dto';
import { ShortNumberPipe } from '../../../../../../common/pipes/short-number/short-number.pipe';
import { CreatorPlatformStatRow, listCreatorPlatformStats } from '../../../../../../common/format/creator-platform-stats';
import { ImageService } from '../../../../../../services/image/image.service';
import { VideoService } from '../../../../../../services/video/video.service';
import { SubmittedPostsService } from '../../../../../../services/submitted-posts/submitted-posts.service';
import {
  assignmentStageIndex,
  BriefKind,
  NextStep,
  stageElapsed,
  videoCounts,
} from '../../assignment-view.helpers';
import { evaluateAnswer, buildScreeningQaRows, Verdict } from '../../screening-qa.util';
import { toEpochMs } from '../application-expiry-bar/application-expiry-bar.helpers';
import { formatStageElapsed } from '../application-stage-progress/application-stage-progress.helpers';
import { ASSIGNMENT_STAGE_STATUSES } from '../../assignment-view.helpers';
import { MbaIcon, MbaIconName } from '../../atoms/mba-icon/mba-icon';
import { VideoStatusPill } from '../../atoms/video-status-pill/video-status-pill';
import { ApplicationCountdown } from '../application-countdown/application-countdown';
import { NextStepCell } from '../next-step-cell/next-step-cell';
import { HireBreakdown } from '../hire-breakdown/hire-breakdown';
import { ui } from '../../assignment-ui';

type VideoState = 'new' | 'seen' | 'chg' | 'done' | 'none';

interface VideoRow {
  title: string;
  state: VideoState;
  thumbnail: string | null;
}

const VIDEO_STATE_LABEL: Record<VideoState, string> = {
  new: 'New · needs review',
  seen: 'Seen',
  chg: 'Changes requested',
  done: 'Approved',
  none: 'Not uploaded yet',
};

const VIDEO_TONE: Record<VideoState, { text: string; dot: string }> = {
  new: { text: 'text-(--app-accent-strong)', dot: 'bg-(--app-accent)' },
  seen: { text: 'text-(--fg-2)', dot: 'bg-(--fg-3)' },
  chg: { text: 'text-(--warn)', dot: 'bg-(--warn-dot)' },
  done: { text: 'text-(--success)', dot: 'bg-(--success)' },
  none: { text: 'text-(--fg-3)', dot: 'bg-(--border-strong)' },
};

const VERDICT_CLASS: Record<Verdict, string> = {
  pass: 'bg-(--success)/10 text-(--success)',
  fail: 'bg-(--danger)/10 text-(--danger)',
  review: 'bg-(--app-accent-soft) text-(--app-accent-strong)',
};

const ACTION_ICON: Record<NextStep['action'], MbaIconName> = {
  remind: 'bell',
  hire: 'user-check',
  view: 'arrow-right',
};

/**
 * Creator drawer: everything about one creator on this brief, read-only apart from the
 * one next step. For an Applied creator that step is the hire itself: the price breakdown
 * and Hire / Decline (see HireBreakdown). Approvals, chat and payment live in View Submission.
 */
@Component({
  selector: 'mbt-creator-side-panel',
  imports: [DecimalPipe, ShortNumberPipe, MbaIcon, VideoStatusPill, ApplicationCountdown, NextStepCell, HireBreakdown],
  templateUrl: './creator-side-panel.html',
  host: {
    style: 'display: contents',
    '(document:keydown)': 'onKeydown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorSidePanel {
  protected readonly ui = ui;
  /** The current stage's bar is striped. */
  protected readonly currentBar =
    'bg-[repeating-linear-gradient(90deg,var(--app-accent)_0_7px,color-mix(in_oklch,var(--app-accent)_35%,#fff)_7px_11px)]';
  protected readonly boxRow = 'flex items-center justify-between gap-3 px-3.5 py-3';
  protected readonly boxRowText = 'flex min-w-0 flex-col gap-[3px]';
  protected readonly boxRowTitle = 'font-(family-name:--font-display) text-[17px] leading-none font-bold';
  private readonly shortNumber = new ShortNumberPipe();
  private readonly imageService = inject(ImageService);
  private readonly videoService = inject(VideoService);
  private readonly submittedPostsService = inject(SubmittedPostsService);

  readonly applicant = input.required<BriefApplicationResponseDto>();
  readonly brief = input<BriefResponseDto | undefined>(undefined);
  readonly kind = input<BriefKind>('ugc');
  readonly stageLabels = input.required<string[]>();
  readonly step = input.required<NextStep>();
  readonly needsYou = input(false);
  readonly nowMs = input.required<number>();
  readonly autoDeclineDays = input<number | null | undefined>(null);
  readonly autoDeclineOn = input(false);
  readonly expired = input(false);
  readonly busy = input(false);
  readonly position = input(0);
  readonly total = input(0);
  readonly canUnhire = input(false);
  readonly channelLabel = input('TikTok');
  readonly costText = input('');
  readonly costNote = input('');
  /** Hire right here, with the price breakdown. False when the brand must first pick networks (Review application). */
  readonly hireInPanel = input(false);

  readonly closed = output<void>();
  readonly previous = output<void>();
  readonly next = output<void>();
  readonly primary = output<void>();
  readonly decline = output<void>();
  readonly hire = output<void>();
  readonly withdraw = output<void>();
  readonly unhire = output<void>();
  readonly openProfile = output<void>();
  readonly copyInvite = output<string>();

  protected readonly creator = computed(() => this.applicant().creator);
  protected readonly stage = computed(() => assignmentStageIndex(this.applicant().applicationStatus) ?? 0);
  protected readonly counts = computed(() => videoCounts(this.applicant()));
  protected readonly actionIcon = computed(() => ACTION_ICON[this.step().action]);
  protected readonly photo = computed(() =>
    this.creator()?.photoURL ? this.imageService.getImageSource(this.creator()!.photoURL, 'gallery') : null,
  );
  protected readonly initial = computed(() => (this.creator()?.firstName ?? '?').charAt(0).toUpperCase());
  protected readonly fullName = computed(() =>
    `${this.creator()?.firstName?.trim() ?? ''} ${this.creator()?.lastName?.trim().charAt(0) ?? ''}`.trim(),
  );
  protected readonly firstName = computed(() => this.creator()?.firstName?.trim() || 'creator');
  protected readonly primaryStats = computed<CreatorPlatformStatRow | null>(
    () => listCreatorPlatformStats(this.creator()?.ssCreator)[0] ?? null,
  );
  protected readonly stats = computed(() => {
    const creator = this.creator();
    const primary = this.primaryStats();
    const count = (value: number | null | undefined) => (value ? String(this.shortNumber.transform(value)) : '\u2014');
    return [
      { label: 'TikTok', value: count(creator?.tikTokFollowers) },
      { label: 'Instagram', value: count(creator?.instaFollowers) },
      // Averages arrive unrounded (983.9215…); at most 2 decimals, trailing zeros dropped.
      { label: 'Avg views', value: count(primary?.averageViews == null ? null : Math.round(primary.averageViews * 100) / 100) },
      { label: 'Eng. rate', value: primary?.averageEngagementRate ? `${primary.averageEngagementRate.toFixed(2).replace(/\.?0+$/, '')}%` : '\u2014' },
    ];
  });
  protected readonly rating = computed(() => {
    const summary = this.creator()?.reviews_summary;
    return summary && summary.average_rating > 0 ? summary : null;
  });

  /** One column per stage: reached, current, and when the creator got there. */
  protected readonly timeline = computed(() => {
    const history = this.applicant().statusHistory ?? [];
    const current = this.stage();
    return this.stageLabels().map((label, index) => {
      let when = '—';
      if (index === current) {
        when = stageElapsed(this.applicant(), this.nowMs()) || '—';
      } else if (index < current) {
        const statuses = ASSIGNMENT_STAGE_STATUSES[index] as number[];
        const first = history
          .filter((event) => statuses.includes(event.status))
          .map((event) => toEpochMs(event.createdDate))
          .filter((ms): ms is number => ms != null)
          .sort((a, b) => a - b)[0];
        when = first != null ? formatStageElapsed(this.nowMs() - first) : '✓';
      }
      return { label, reached: index <= current, current: index === current, when };
    });
  });

  protected readonly qa = computed(() =>
    buildScreeningQaRows(this.brief(), this.applicant()).map((row) => ({
      ...row,
      verdict: evaluateAnswer(row.expected, row.answer),
    })),
  );

  /* ── Lazy data: videos (stage ≥ Hired) and posts (influence, stage ≥ Posting) ── */
  protected readonly videos = signal<VideoResponseDto[] | null>(null);
  protected readonly posts = signal<SubmittedPostDto[] | null>(null);

  protected readonly videoRows = computed<VideoRow[]>(() => {
    const uploaded = (this.videos() ?? []).filter((video) => !(video as { isDeleted?: boolean }).isDeleted);
    const pieces = this.brief()?.pieces ?? [];
    const required = Math.max(this.brief()?.videosPerCreator ?? 0, pieces.length, uploaded.length);
    return Array.from({ length: required }, (_, index) => {
      const video = uploaded[index];
      const pieceName = pieces[index]?.label;
      const title = `Video ${index + 1}${video?.pieceLabel || pieceName ? ` · ${video?.pieceLabel || pieceName}` : ''}`;
      return {
        title,
        state: video ? videoState(video.status) : 'none',
        thumbnail: video?.thumbnailURL || video?.thumbnail || null,
      };
    });
  });

  protected readonly approvedCount = computed(() => this.videoRows().filter((row) => row.state === 'done').length);
  protected readonly livePost = computed(() =>
    (this.posts() ?? []).find((post) => post.status === SubmittedPostStatusEnum.Valid) ?? (this.posts() ?? [])[0] ?? null,
  );
  protected readonly sparkCode = computed(() => this.livePost()?.adCode || null);

  constructor() {
    effect((onCleanup) => {
      const applicant = this.applicant();
      const briefId = this.brief()?.id;
      const stage = assignmentStageIndex(applicant.applicationStatus) ?? 0;
      this.videos.set(null);
      this.posts.set(null);
      const subs = new Subscription();
      if (stage >= 2 && briefId && applicant.creator?.blazeFirebaseUid) {
        subs.add(this.videoService.getCreatorBriefVideos(applicant.creator.blazeFirebaseUid, briefId).subscribe({
          next: (videos) => this.videos.set(videos ?? []),
          error: () => this.videos.set([]),
        }));
      }
      if (this.kind() === 'influence' && stage >= 4) {
        subs.add(this.submittedPostsService.getSubmittedPostsForBriefApplication(applicant.id).subscribe({
          next: (posts) => this.posts.set(posts ?? []),
          error: () => this.posts.set([]),
        }));
      }
      onCleanup(() => subs.unsubscribe());
    });
  }

  protected videoLabel(state: VideoState): string {
    return VIDEO_STATE_LABEL[state];
  }

  protected postStatusText(post: SubmittedPostDto): string {
    const when = toEpochMs(post.createdDate);
    const ago = when == null ? '' : ` ${formatStageElapsed(this.nowMs() - when)}`;
    return post.status === SubmittedPostStatusEnum.Valid ? `Live${ago}` : `${post.status}${ago}`;
  }

  protected videoTone(state: VideoState): { text: string; dot: string } {
    return VIDEO_TONE[state];
  }

  protected verdictClass(verdict: Verdict): string {
    return VERDICT_CLASS[verdict];
  }

  protected verdictLabel(verdict: Verdict): string {
    return verdict === 'pass' ? '✓ Qualifies' : verdict === 'fail' ? '✗ Below bar' : 'Manual review';
  }

  protected onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    // A modal on top owns the keyboard.
    if (target?.closest('input, textarea, select') || document.querySelector('[data-mba-modal]')) {
      return;
    }
    if (event.key === 'Escape') {
      this.closed.emit();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.next.emit();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.previous.emit();
    }
  }
}

function videoState(status: number): VideoState {
  switch (status) {
    case 0:
      return 'new';
    case 1:
      return 'seen';
    case 2:
      return 'done';
    case 3:
      return 'chg';
    default:
      return 'seen';
  }
}
